import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import GroupsIcon from "@mui/icons-material/Groups";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {useNavigate} from "react-router";

const PILLARS = [
    {
        icon: <MenuBookIcon sx={{fontSize: 36, color: "primary.main"}} />,
        title: "Kunnskap som frigjør",
        description:
            "Lær hvorfor hjernen produserer bekymringstanker, og hvorfor forsøk på å stoppe eller undertrykke dem ofte forsterker uroligheten.",
    },
    {
        icon: <SelfImprovementIcon sx={{fontSize: 36, color: "primary.main"}} />,
        title: "Konkrete verktøy",
        description:
            "Få praktiske, velprøvde teknikker som oppmerksomt nærvær, avgrenset bekymringstid og bevisst skifte av fokus i hverdagen.",
    },
    {
        icon: <GroupsIcon sx={{fontSize: 36, color: "primary.main"}} />,
        title: "Trygge kursgrupper",
        description:
            "Møt andre i samme situasjon i et støttende fellesskap ledet av autoriserte kursholdere lokalt i din kommune, frisklivssentral eller DPS.",
    },
];

export const AboutSection = () => {
    const navigate = useNavigate();

    return (
        <Box
            component="section"
            id="om-kurset"
            aria-labelledby="about-title"
            sx={{
                py: {xs: 4, md: 6},
                width: "100%",
                maxWidth: "100%",
            }}
        >
            <Box sx={{textAlign: "center", mb: 4, maxWidth: 760, mx: "auto"}}>
                <Typography
                    id="about-title"
                    variant="h4"
                    component="h2"
                    sx={{
                        fontWeight: 800,
                        fontSize: {xs: "1.6rem", sm: "2rem", md: "2.25rem"},
                        color: "primary.main",
                        mb: 1.5,
                        letterSpacing: "-0.01em",
                    }}
                >
                    Hva er Kurs i Bekymringsmestring?
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{fontSize: {xs: "0.975rem", sm: "1.05rem"}, lineHeight: 1.7}}
                >
                    Kurset bygger på metakognitive, mindfulnessbaserte og emosjonsfokuserte prinsipper som gir deg verktøy til å mestre grubling, uro og tankespinn i hverdagen.
                </Typography>
            </Box>

            {/* 3 Pillars Grid */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))"},
                    gap: 3,
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                }}
            >
                {PILLARS.map((pillar) => (
                    <Card
                        key={pillar.title}
                        sx={{
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
                            width: "100%",
                            maxWidth: "100%",
                            minWidth: 0,
                            boxSizing: "border-box",
                            transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                            "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: "0 6px 16px rgba(40, 82, 56, 0.1)",
                                borderColor: "rgba(40, 82, 56, 0.25)",
                            },
                        }}
                    >
                        <CardContent sx={{p: {xs: 2, sm: 3}, display: "flex", flexDirection: "column", height: "100%", width: "100%", maxWidth: "100%", minWidth: 0}}>
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 2.5,
                                    bgcolor: "rgba(40, 82, 56, 0.08)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mb: 2,
                                }}
                            >
                                {pillar.icon}
                            </Box>
                            <Typography
                                variant="h6"
                                component="h3"
                                sx={{fontWeight: 700, color: "text.primary", mb: 1}}
                            >
                                {pillar.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{lineHeight: 1.6, flexGrow: 1}}
                            >
                                {pillar.description}
                            </Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            <Box sx={{mt: 4, textAlign: "center"}}>
                <Button
                    variant="outlined"
                    color="primary"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate("/about")}
                    sx={{
                        borderRadius: 3,
                        px: {xs: 2, sm: 3.5},
                        py: 1.25,
                        maxWidth: "100%",
                        whiteSpace: {xs: "normal", sm: "nowrap"},
                        fontWeight: 700,
                        textTransform: "none",
                        fontSize: "0.95rem",
                        borderColor: "rgba(40, 82, 56, 0.4)",
                        "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: "rgba(40, 82, 56, 0.05)",
                        },
                    }}
                >
                    Les hele artikkelen om kurset
                </Button>
            </Box>
        </Box>
    );
};
